<template>
  <div class="m-t-2 m-b-2">
    <template v-if="shipping.address && billingAddress">
      <el-col :span="24">
        <el-tag type="info">
          {{capitalize(order.financial_status).replace(/_/g, ' ')}}
        </el-tag>
      </el-col>
      <article class="row">
        <div class="col-md-6">
          <order-address title="Shipping Address"
                         :$v="$v.shipping.address"
                         :required="true"
                         :disabled="disabled"
                         :region="region"
                         :country="shippingAddress.country.get()"
                         v-on:update:country="shippingAddress.country.set"
                         :company="shippingAddress.company.get()"
                         v-on:update:company="shippingAddress.company.set"
                         :first_name="shippingAddress.first_name.get()"
                         v-on:update:first_name="shippingAddress.first_name.set"
                         :last_name="shippingAddress.last_name.get()"
                         v-on:update:last_name="shippingAddress.last_name.set"
                         :address1="shippingAddress.address1.get()"
                         v-on:update:address1="shippingAddress.address1.set"
                         :address2="shippingAddress.address2.get()"
                         v-on:update:address2="shippingAddress.address2.set"
                         :city="shippingAddress.city.get()"
                         v-on:update:city="shippingAddress.city.set"
                         :province="shippingAddress.province.get()"
                         v-on:update:province="shippingAddress.province.set"
                         :zip="shippingAddress.zip.get()"
                         v-on:update:zip="shippingAddress.zip.set"
                         :phone="shippingAddress.phone.get()"
                         v-on:update:phone="shippingAddress.phone.set"/>
        </div>
        <div class="col-md-6">
          <order-address :disabled="true"
                         :region="region"
                         :country.sync="billingAddress.country"
                         :company.sync="billingAddress.company"
                         :first_name.sync="billingAddress.first_name"
                         :last_name.sync="billingAddress.last_name"
                         :address1.sync="billingAddress.address1"
                         :address2.sync="billingAddress.address2"
                         :city.sync="billingAddress.city"
                         :province.sync="billingAddress.province"
                         :zip.sync="billingAddress.zip"
                         :phone.sync="billingAddress.phone"
                         title="Billing Address"/>
        </div>
      </article>
      <article class="row">
        <div class="col-md-6">
          <order-product-search/>
        </div>
        <div class="col-md-6">
          <order-discount-add
              :disabled="isDiscountDisabled"
              :typeSelected="discountOrder.selectedType.get()"
              v-on:update:typeSelected="discountOrder.selectedType.set"
              :discountCode="discountOrder.discountCode.get()"
              v-on:update:discountCode="discountOrder.discountCode.set"
              :fixedValue="discountOrder.fixedValue.get()"
              v-on:update:fixedValue="discountOrder.fixedValue.set"
              :percentValue="discountOrder.percentValue.get()"
              v-on:update:percentValue="discountOrder.percentValue.set"
              v-on:applyDiscountCode="applyDiscountCode"
              v-on:applyFixed="applyFixed"
              v-on:applyPercent="applyPercent"/>
        </div>
      </article>
      <article class="row">
        <div class="col-md-12">
          <order-products/>
        </div>
      </article>
      <article class="row m-t-1">
        <div class="col-md-12">
          <order-actions :order="order" :products="products" :region="region" :d365Order="d365Order"></order-actions>
        </div>
      </article>
      <article class="row">
        <div class="col-md-9">
          <order-payment-details :payment="payment"
                                 :maxEligibleAmount="maxEligibleAmount"
                                 :quantity="quantity"
                                 :products="products"/>
        </div>
        <div class="col-md-3">
          <order-note :note.sync="note"/>
        </div>
      </article>
      <template v-if="order.refunds">
        <div v-for="refund in order.refunds">
          <order-refund-transactions
            :key="refund.id"
            :refund="refund"
          />
        </div>
      </template>
      <hr>
      <article class="row">
        <div class="col-md-12">
          <order-save :isLoading="isLoading"/>
        </div>
      </article>
    </template>
    <app-loader v-else-if="isLoading"/>
    <el-dialog :visible.sync="isD365StatusVisible"
               width="30%">
      <template slot="title">
        <h4>
          <p>{{d365Message}}</p>
          <p>{{d365Issue}}</p>
          <p>Please see below:</p>
        </h4>
      </template>
      <d365-detail-table :d365Order="d365Order"/>
      <span slot="footer" class="dialog-footer">
        <el-button @click="isD365StatusVisible = false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';
  import {
    required,
    requiredIf,
    minLength,
    minValue,
    maxValue,
    between,
    decimal,
    numeric,
  } from 'vuelidate/lib/validators';
  import {
    ORDER_GET_DISABLED_STATUS_AP,
    ORDER_SET_SHIPPING_ADDRESS,
    ORDER_DISCOUNT_APPLY_CODE,
    ORDER_SET_DISCOUNT_TYPE,
    ORDER_SET_DISCOUNT_CODE,
    ORDER_SET_DISCOUNT_FIXED,
    ORDER_SET_DISCOUNT_PERCENT,
    ORDER_DISCOUNT_APPLY_FIXED,
    ORDER_DISCOUNT_APPLY_PERCENT,
    ORDER_SET_NOTE,
    ORDER_GET_PAYMENT_LINE_ITEMS_COUNT,
    ORDER_GET_SHIPPING_TOTAL_PRICE,
    ORDER_GET_IS_DISCOUNT_DISABLED
  } from '../../../store/types';
  import {
    LOGON_CANNOT_MODIFY_ORDER,
  } from '../../../lang/en/notify';
  import AppLoader from '../../ui/AppLoader';
  import OrderSave from './OrderUpdateControls';
  import OrderProducts from './OrderUpdateProducts';
  import OrderProductSearch from './OrderUpdateProductSearch';
  import OrderConfirmationPage from './OrderUpdateConfirmationPage';
  import OrderPaymentDetails from './Payment/OrderUpdatePayment';
  import OrderNote from '../Shared/OrderNote';
  import OrderAddress from '../Shared/OrderAddress';
  import OrderActions from "../Shared/OrderActions";
  import OrderDiscountAdd from '../Shared/OrderDiscountAdd';
  import D365DetailTable from '../Shared/D365DetailTable';
  import OrderRefundTransactions from '../Shared/OrderRefundTransactions';
  import {capitalize} from '../../../filters';

  export default {
    components: {
      OrderRefundTransactions,
      AppLoader,
      OrderNote,
      OrderSave,
      OrderAddress,
      OrderActions,
      OrderProducts,
      OrderDiscountAdd,
      OrderProductSearch,
      OrderPaymentDetails,
      OrderConfirmationPage,
      D365DetailTable,
    },
    computed: {
      ...mapState({
        isLoading: state => state.order.loading,
        order: state => state.order.order,
        region: state => state.order.region,
        billingAddress: state => state.order.order.billing_address,
        shipping: state => state.order.shipping,
        discount: state => state.order.discount,
        products: state => state.order.products,
        getNote: state => state.order.note,
        payment: state => state.order.payment,
        maxEligibleAmount: state => Number(state.order.maxEligibleAmount),
        d365Order: state => state.order.d365Order,
        d365Issue: state => state.order.d365Error,
      }),
      ...mapGetters({
        disabled: ORDER_GET_DISABLED_STATUS_AP,
        quantity: ORDER_GET_PAYMENT_LINE_ITEMS_COUNT,
        shippingPrice: ORDER_GET_SHIPPING_TOTAL_PRICE,
        isDiscountDisabled: ORDER_GET_IS_DISCOUNT_DISABLED
      }),
      shippingAddress() {
        return {
          country: {
            get: () => {
              return this.shipping.address.country;
            },
            set: (value) => {
              this.setShippingAddressProperty({country: value});
              this.$v.shipping.address.country.$touch();
            },
          },
          company: {
            get: () => {
              return this.shipping.address.company;
            },
            set: (value) => {
              this.setShippingAddressProperty({company: value});
              this.$v.shipping.address.company.$touch();
            },
          },
          first_name: {
            get: () => {
              return this.shipping.address.first_name;
            },
            set: (value) => {
              this.setShippingAddressProperty({first_name: value});
              this.$v.shipping.address.first_name.$touch();
            },
          },
          last_name: {
            get: () => {
              return this.shipping.address.last_name;
            },
            set: (value) => {
              this.setShippingAddressProperty({last_name: value});
              this.$v.shipping.address.last_name.$touch();
            },
          },
          address1: {
            get: () => {
              return this.shipping.address.address1;
            },
            set: (value) => {
              this.setShippingAddressProperty({address1: value});
              this.$v.shipping.address.address1.$touch();
            },
          },
          address2: {
            get: () => {
              return this.shipping.address.address2;
            },
            set: (value) => {
              this.setShippingAddressProperty({address2: value});
              this.$v.shipping.address.address2.$touch();
            },
          },
          city: {
            get: () => {
              return this.shipping.address.city;
            },
            set: (value) => {
              this.setShippingAddressProperty({city: value});
              this.$v.shipping.address.city.$touch();
            },
          },
          province: {
            get: () => {
              return this.shipping.address.province;
            },
            set: (value) => {
              this.setShippingAddressProperty({province: value});
              this.$v.shipping.address.province.$touch();
            },
          },
          zip: {
            get: () => {
              return this.shipping.address.zip;
            },
            set: (value) => {
              this.setShippingAddressProperty({zip: value});
              this.$v.shipping.address.zip.$touch();
            },
          },
          phone: {
            get: () => {
              return this.shipping.address.phone;
            },
            set: (value) => {
              this.setShippingAddressProperty({phone: value});
              this.$v.shipping.address.phone.$touch();
            },
          },
        };
      },
      discountOrder() {
        return {
          selectedType: {
            get: () => {
              return this.discount.typeSelected;
            },
            set: (value) => {
              this.setType(value);
            },
          },
          discountCode: {
            get: () => {
              return this.discount.code;
            },
            set: (value) => {
              this.setSearch(value);
              this.$v.discount.code.$touch();
            },
          },
          fixedValue: {
            get: () => {
              return this.discount.fixed;
            },
            set: (value) => {
              this.setFixed(value);
              this.$v.discount.fixed.$touch();
            },
          },
          percentValue: {
            get: () => {
              return this.discount.percent;
            },
            set: (value) => {
              this.setPercent(value);
              this.$v.discount.percent.$touch();
            },
          },
        };
      },
      note: {
        get() {
          return this.getNote;
        },
        set(note) {
          this.$v.$touch();
          this.setNote(note);
        },
      },
    },
    methods: {
      ...mapMutations({
        setShippingAddressProperty: ORDER_SET_SHIPPING_ADDRESS,
        setSearch: ORDER_SET_DISCOUNT_CODE,
        setType: ORDER_SET_DISCOUNT_TYPE,
        setFixed: ORDER_SET_DISCOUNT_FIXED,
        setPercent: ORDER_SET_DISCOUNT_PERCENT,
        setNote: ORDER_SET_NOTE,
      }),
      ...mapActions({
        applyDiscountCode: ORDER_DISCOUNT_APPLY_CODE,
        applyFixed: ORDER_DISCOUNT_APPLY_FIXED,
        applyPercent: ORDER_DISCOUNT_APPLY_PERCENT,
      }),
      capitalize
    },
    watch: {
      d365Issue: function(d365Issue) {
        if (d365Issue) {
          this.isD365StatusVisible = true;
        }
      },
    },
    data() {
      return {
        d365Message: LOGON_CANNOT_MODIFY_ORDER,
        isD365StatusVisible: false,
      };
    },
    provide() {
      return {
        $v: this.$v,
      };
    },
    validations() {
      return {
        shipping: {
          customPrice: {
            minValue: minValue(0),
            maxValue: (value, shipping) => {
              if (shipping.selected.id !== 0) {
                return true;
              }
              return value <= this.shippingPrice;
            }
          },
          customSelected: {
            id: {
              required: requiredIf(function() {
                return this.shipping.selected.id === 0;
              }),
            },
          },
          selected: {
            id: {
              required,
            },
          },
          address: {
            country: {
              required,
            },
            company: {},
            first_name: {
              required,
            },
            last_name: {
              required,
            },
            address1: {
              required,
            },
            address2: {},
            city: {
              required,
            },
            province: {
              required: requiredIf(function({country}) {
                if(country) {
                  const isCanada = country.toLowerCase() === 'Canada'.toLowerCase();
                  const isUSA = country.toLowerCase() === 'United States'.toLowerCase();
                  return isCanada || isUSA;
                }
                return false;
              })
            },
            zip: {
              required: requiredIf(function({country}) {
                if(country) {
                  const isCanada = country.toLowerCase() === 'Canada'.toLowerCase();
                  const isUSA = country.toLowerCase() === 'United States'.toLowerCase();
                  return isCanada || isUSA;
                }
                return false;
              })
            },
            phone: {
              required,
            },
          },
        },
        discount: {
          fixed: {
            decimal,
            minValue: minValue(0.00),
          },
          percent: {
            decimal,
            between: between(0.00, 100.00),
          },
          code: {},
        },
        products: {
          items: {
            required,
            minLength: minLength(1),
            $each: {
              quantity: {
                required,
                numeric,
                minValue: minValue(0),
              },
            },
          },
        },
        note: {
          required,
        },
        payment: {
          lineItems: {
            minLength: minLength(1),
          },
          totalPrice: {
            required,
            maxValue: maxValue(this.maxEligibleAmount),
            minValue: (value, {subtotalPrice, totalTax}) => {
              let shippingPrice = Number(this.shipping.selected.price);
              if (this.shipping.id === 0) {
                shippingPrice = Number(this.shipping.customPrice);
              }

              const totalPriceCalculated = (shippingPrice + subtotalPrice +
                  totalTax);

              return (totalPriceCalculated - value) < 1;
            },
          },
        },
      };
    },
  };
</script>
